import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
} from '@mui/material'
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material'
import { request } from '../common/services/request'
import { formatLocalDateTime } from '../common/utils'
import { API_ENDPOINTS } from '../common/endpoints'
import VitalsChart from '../components/VitalsChart'
import type { Schedule, ScheduleRun, MetricType, DiseaseProfile } from '../common/types'
import { DISEASE_PROFILES, METRIC_TYPES } from '../common/types'

function ScheduleDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [schedule, setSchedule] = useState<Schedule | null>(null)
  const [scheduleVitals, setScheduleVitals] = useState<any[]>([])
  const [loadingScheduleVitals, setLoadingScheduleVitals] = useState(false)
  const [runs, setRuns] = useState<ScheduleRun[]>([])
  const [runsPage, setRunsPage] = useState(1)
  const [runsPageSize] = useState(50)
  const [runsTotal, setRunsTotal] = useState<number | null>(null)
  const [loadingMoreRuns, setLoadingMoreRuns] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editForm, setEditForm] = useState<Partial<Schedule>>({})
  const [editUrlError, setEditUrlError] = useState<string | null>(null)
  
  const [viewVitalsDialogOpen, setViewVitalsDialogOpen] = useState(false)
  const [selectedRun, setSelectedRun] = useState<ScheduleRun | null>(null)
  const [runVitals, setRunVitals] = useState<any[]>([])
  const [loadingRunVitals, setLoadingRunVitals] = useState(false)

  const fetchData = async () => {
    try {
      setLoading(true)
      const client = request()
      const res = await client.get<Schedule>(API_ENDPOINTS.SCHEDULES.BY_ID(id!))
      setSchedule(res.data)

      try {
        setLoadingScheduleVitals(true)
        const windowDays = res.data.window_days ?? 7
        const vitalsRes = await client.get(API_ENDPOINTS.SCHEDULES.BASE + `/${id}/vitals?window_days=${windowDays}`)
        setScheduleVitals(vitalsRes.data.vitals || [])
      } catch (e) {
        setScheduleVitals([])
      } finally {
        setLoadingScheduleVitals(false)
      }

      const runsRes = await client.get(API_ENDPOINTS.SCHEDULES.RUNS(id!), {
        params: { page: 1, page_size: runsPageSize },
      })
      const items = runsRes.data.items || runsRes.data || []
      setRuns(items)
      setRunsTotal(runsRes.data.total ?? null)
      setRunsPage(1)
    } catch (e: any) {
      setError(e.response?.data?.detail || e.message || 'Failed to load schedule')
    } finally {
      setLoading(false)
    }
  }

  const handlePause = async () => {
    if (!schedule) return
    try {
      const client = request()
      await client.post(API_ENDPOINTS.SCHEDULES.PAUSE(schedule.id))
      // refresh schedule and runs
      await fetchData()
    } catch (e: any) {
      setError(e.response?.data?.detail || e.message || 'Failed to pause schedule')
    }
  }

  const handleResume = async () => {
    if (!schedule) return
    try {
      const client = request()
      await client.post(API_ENDPOINTS.SCHEDULES.RESUME(schedule.id))
      // refresh schedule and runs
      await fetchData()
    } catch (e: any) {
      setError(e.response?.data?.detail || e.message || 'Failed to resume schedule')
    }
  }

  const handleDelete = async () => {
    if (!schedule) return
    if (!confirm('Are you sure you want to delete this schedule?')) return
    try {
      const client = request()
      await client.delete(API_ENDPOINTS.SCHEDULES.BY_ID(schedule.id))
      navigate('/')
    } catch (e: any) {
      setError(e.response?.data?.detail || e.message || 'Failed to delete schedule')
    }
  }

  useEffect(() => {
    fetchData()
  }, [id])

  const handleOpenEdit = () => {
    if (!schedule) return
    setEditForm({
      name: schedule.name,
      status: schedule.status,
      profile: schedule.profile,
      interval: schedule.interval,
      window_days: schedule.window_days,
      metrics: schedule.metrics,
      seed: schedule.seed ?? undefined,
      noise_level: schedule.noise_level,
      dropout_rate: schedule.dropout_rate ?? 0.0,
      destination_url: schedule.destination_url,
    })
    setEditDialogOpen(true)
  }

  const handleSaveEdit = async () => {
    if (!schedule) return
    
    // Validate URL if it's being updated
    if (editForm.destination_url !== undefined) {
      const url = editForm.destination_url?.trim() || ''
      if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
        setEditUrlError('URL must start with http:// or https://')
        return
      }
    }
    
    try {
      const client = request()
      await client.patch(API_ENDPOINTS.SCHEDULES.BY_ID(schedule.id), editForm)
      setEditDialogOpen(false)
      setEditUrlError(null)
      await fetchData()
    } catch (e: any) {
      setError(e.response?.data?.detail || e.message || 'Failed to update schedule')
    }
  }

  // Poll runs periodically so in-progress runs appear on the page
  useEffect(() => {
    if (!id) return
    let mounted = true

    const pollRuns = async () => {
      try {
        const client = request()
        const runsRes = await client.get(API_ENDPOINTS.SCHEDULES.RUNS(id), {
          params: { page: 1, page_size: runsPageSize },
        })
        if (!mounted) return
        const items = runsRes.data.items || runsRes.data || []
        setRuns((prev) => {
          // replace first page results while keeping any already-loaded subsequent pages
          const start = 0
          const next = [...prev]
          for (let i = 0; i < items.length; i++) {
            next[start + i] = items[i]
          }
          return next.length ? next : items
        })
        setRunsTotal(runsRes.data.total ?? null)
      } catch (e) {
      }
    }

    const iv = setInterval(pollRuns, 5000)
    pollRuns()

    return () => {
      mounted = false
      clearInterval(iv)
    }
  }, [id])

  // Load additional pages of runs
  const fetchRunsPage = async (page: number) => {
    if (!id) return
    try {
      setLoadingMoreRuns(true)
      const client = request()
      const runsRes = await client.get(API_ENDPOINTS.SCHEDULES.RUNS(id), {
        params: { page, page_size: runsPageSize },
      })
      const items = runsRes.data.items || runsRes.data || []
      if (page === 1) {
        setRuns(items)
      } else {
        setRuns((prev) => [...prev, ...items])
      }
      setRunsTotal(runsRes.data.total ?? null)
      setRunsPage(page)
    } catch (e) {
      // ignore
    } finally {
      setLoadingMoreRuns(false)
    }
  }

  const getRunStatusColor = (status: string | undefined) => {
    if (!status) return 'grey.400'
    const s = String(status).toLowerCase()
    if (s === 'completed') return 'success.main'
    if (s === 'failed' || s === 'error') return 'error.main'
    // pending, queued, running -> yellow
    if (s === 'pending' || s === 'queued' || s === 'running') return 'warning.main'
    return 'grey.400'
  }

  const handleViewRunVitals = async (run: ScheduleRun) => {
    setSelectedRun(run)
    setViewVitalsDialogOpen(true)
    setLoadingRunVitals(true)
    
    try {
      const client = request()
      const res = await client.get(API_ENDPOINTS.SCHEDULES.RUN_VITALS(id!, run.id))
      setRunVitals(res.data.vitals || [])
    } catch (e: any) {
      console.error('Failed to load run vitals:', e)
      setRunVitals([])
    } finally {
      setLoadingRunVitals(false)
    }
  }

  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>

  if (error) return <Alert severity="error">{error}</Alert>

  if (!schedule) return <Typography>No schedule found</Typography>

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/') }>
            Back
          </Button>
          <Box>
            <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
              Schedule: {schedule.name || `#${schedule.id}`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Interval: {schedule.interval} · Window: {schedule.window_days} days · Dropout: {typeof schedule.dropout_rate !== 'undefined' ? (parseFloat(String(schedule.dropout_rate)) * 100).toFixed(0) + '%' : '0%'} · Status: {schedule.status}
            </Typography>
          </Box>
        </Box>
        <Box>
          {schedule.status === 'active' ? (
            <Button variant="outlined" color="warning" sx={{ mr: 1 }} onClick={handlePause}>
              Pause
            </Button>
          ) : (
            <Button variant="outlined" color="success" sx={{ mr: 1 }} onClick={handleResume}>
              Resume
            </Button>
          )}
          <Button variant="contained" onClick={() => {
            // Trigger run-now
            const client = request()
            client.post(API_ENDPOINTS.SCHEDULES.RUN_NOW(schedule.id)).then(() => fetchData())
          }}>
            Run Now
          </Button>
          <Button variant="outlined" sx={{ ml: 1 }} onClick={handleOpenEdit}>Edit</Button>
          <Button variant="contained" color="error" sx={{ ml: 1 }} onClick={handleDelete}>Delete</Button>
        </Box>
      </Box>

      {/* Schedule-level recent vitals chart */}
      <Box mb={2}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Recent Vitals (sliding window)</Typography>
          {loadingScheduleVitals ? (
            <Box display="flex" justifyContent="center" p={2}><CircularProgress /></Box>
          ) : scheduleVitals.length > 0 ? (
            <>
              <Box display="flex" gap={3} alignItems="center" mb={1}>
                {(() => {
                  const recent = [...scheduleVitals].sort((a: any, b: any) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()).slice(0,3)
                  return recent.map((v: any, idx: number) => (
                    <Box key={idx}>
                      <Typography variant="caption" color="text.secondary">{v.metric.toUpperCase()}</Typography>
                      <Typography variant="h6">{v.value}{v.unit ? ` ${v.unit}` : ''}</Typography>
                      <Typography variant="caption" color="text.secondary">{formatLocalDateTime(v.timestamp)}</Typography>
                    </Box>
                  ))
                })()}
              </Box>
              {/* Truncate to most recent 100 points and ensure chronological order for the chart */}
              {(() => {
                const sortedAsc = [...scheduleVitals].sort((a: any, b: any) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
                const last100 = sortedAsc.slice(-100)
                return <VitalsChart vitals={last100} height={220} windowDays={schedule?.window_days ?? 7} interval={schedule?.interval} />
              })()}
            </>
          ) : (
            <Typography color="text.secondary">No recent vitals available for this schedule</Typography>
          )}
        </Paper>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, gap: 1, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="subtitle1">Configuration</Typography>
            <Typography variant="body2"><b>Name</b>: {schedule.name}</Typography>
            <Typography variant="body2"><b>Patient</b>: {schedule.openemr_patient_uuid}</Typography>
            <Typography variant="body2"><b>Metrics</b>: {schedule.metrics.join(', ')}</Typography>
              <Typography variant="body2"><b>Destination</b>: {schedule.destination_url || '-'}</Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, gap: 1, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="subtitle1">Metadata</Typography>
            <Typography variant="body2"><b>Created</b>: {formatLocalDateTime(schedule.created_at)}</Typography>
            <Typography variant="body2"><b>Updated</b>: {formatLocalDateTime(schedule.updated_at)}</Typography>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper>
            <Box sx={{ maxHeight: 360, overflow: 'auto' }} onScroll={(e) => {
              const t = e.currentTarget as HTMLElement
              if (loadingMoreRuns) return
              if (runsTotal !== null && runs.length >= runsTotal) return
              const scrollBottom = t.scrollHeight - t.scrollTop - t.clientHeight
              if (scrollBottom < 50) {
                // near bottom -> load next page
                fetchRunsPage(runsPage + 1)
              }
            }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Started</TableCell>
                      <TableCell>Completed</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Generated</TableCell>
                      <TableCell>Pushed</TableCell>
                      <TableCell>Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(runs?.length ?? 0) === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">No runs</TableCell>
                      </TableRow>
                    ) : (
                      runs.map((r: any) => (
                        <TableRow 
                          key={r.id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => handleViewRunVitals(r)}
                        >
                          <TableCell>{r.id}</TableCell>
                          <TableCell>{formatLocalDateTime(r.started_at)}</TableCell>
                          <TableCell>{formatLocalDateTime(r.completed_at)}</TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center">
                              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: getRunStatusColor(r.status), mr: 1 }} />
                              <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>{r.status}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>{r.vitals_generated}</TableCell>
                          <TableCell>{r.vitals_pushed}</TableCell>
                          <TableCell style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.error_message || '-'}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              {loadingMoreRuns && (
                <Box display="flex" justifyContent="center" p={1}>
                  <CircularProgress size={20} />
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Schedule</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                value={editForm.name || ''}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                label="Status"
                value={editForm.status || 'active'}
                onChange={(e: any) => setEditForm({ ...editForm, status: e.target.value })}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="paused">Paused</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                label="Profile"
                value={editForm.profile || 'normal'}
                onChange={(e) => setEditForm({ ...editForm, profile: e.target.value as DiseaseProfile })}
              >
                {DISEASE_PROFILES.map((p) => (
                  <MenuItem key={p.value} value={p.value}>
                    {p.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                label="Interval"
                value={editForm.interval || '6h'}
                onChange={(e) => setEditForm({ ...editForm, interval: e.target.value })}
              >
                <MenuItem value="1m">Every 1 minute</MenuItem>
                <MenuItem value="10m">Every 10 minutes</MenuItem>
                <MenuItem value="30m">Every 30 minutes</MenuItem>
                <MenuItem value="1h">Every hour</MenuItem>
                <MenuItem value="6h">Every 6 hours</MenuItem>
                <MenuItem value="12h">Every 12 hours</MenuItem>
                <MenuItem value="1d">Every day</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Window Days"
                value={editForm.window_days || 7}
                onChange={(e) => setEditForm({ ...editForm, window_days: parseInt(e.target.value) || 7 })}
                inputProps={{ min: 1, max: 365 }}
                helperText="Days of historical data in sliding window"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Metrics"
                value={editForm.metrics || []}
                onChange={(e) =>
                  setEditForm({
                    ...editForm,
                    metrics: typeof e.target.value === 'string'
                      ? [e.target.value as MetricType]
                      : e.target.value as MetricType[],
                  })
                }
                SelectProps={{ multiple: true }}
              >
                {METRIC_TYPES.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label} ({m.unit})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Seed"
                value={editForm.seed ?? ''}
                onChange={(e) => setEditForm({ ...editForm, seed: e.target.value ? parseInt(e.target.value) : undefined })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Noise Level"
                value={editForm.noise_level ?? 0.1}
                onChange={(e) => setEditForm({ ...editForm, noise_level: parseFloat(e.target.value) || 0.1 })}
                inputProps={{ min: 0, max: 1, step: 0.1 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Dropout Rate"
                value={editForm.dropout_rate ?? 0.0}
                onChange={(e) => setEditForm({ ...editForm, dropout_rate: parseFloat(e.target.value) || 0.0 })}
                inputProps={{ min: 0, max: 1, step: 0.05 }}
                helperText="0 = no dropout, 1 = drop all"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Destination URL"
                value={editForm.destination_url || ''}
                onChange={(e) => {
                  setEditForm({ ...editForm, destination_url: e.target.value })
                  setEditUrlError(null)
                }}
                placeholder="https://your-server.example.com/vitals"
                helperText={editUrlError || "URL to forward generated vitals to"}
                error={!!editUrlError}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* View Run Vitals Dialog */}
      <Dialog 
        open={viewVitalsDialogOpen} 
        onClose={() => setViewVitalsDialogOpen(false)}
        maxWidth="md"
      >
        <DialogTitle>
          Schedule Run #{selectedRun?.id} - Vitals
          {selectedRun && (
            <Typography variant="caption" display="block" color="text.secondary">
              Date: {formatLocalDateTime(selectedRun.started_at)} | 
              Generated: {selectedRun.vitals_generated} | 
              Pushed: {selectedRun.vitals_pushed}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {loadingRunVitals ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
              <CircularProgress />
            </Box>
          ) : runVitals.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Timestamp</TableCell>
                    <TableCell>Metric</TableCell>
                    <TableCell align="right">Value</TableCell>
                    <TableCell>Unit</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {runVitals.map((v: any) => (
                    <TableRow key={v.id}>
                      <TableCell>{formatLocalDateTime(v.timestamp)}</TableCell>
                      <TableCell>{(v.metric || '').toUpperCase()}</TableCell>
                      <TableCell align="right">{v.value}</TableCell>
                      <TableCell>{v.unit || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
              {selectedRun?.error_message ? (
                <Typography color="error">{selectedRun.error_message}</Typography>
              ) : (
                <Typography color="text.secondary">No vitals data available for this run</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewVitalsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ScheduleDetail
